// UI rendering module for ChatWidget
import { renderMarkdown, escapeHtml } from './markdown.js';
import { formatTime } from '../utils/helpers.js';
import { saveManuallyClosed } from './state.js';

/**
 * Check if a bot icon value is an image URL (vs emoji)
 */
export function isImageIcon(iconValue) {
    if (!iconValue) return false;
    return iconValue.startsWith('/') || iconValue.startsWith('http');
}

/**
 * Check if a URL points to an SVG file
 */
function isSvgUrl(url) {
    if (!url) return false;
    return url.toLowerCase().endsWith('.svg');
}

/**
 * Render bot icon as either emoji text or an img tag
 * @param {string} iconValue - Either an emoji or an image URL
 * @param {string} className - Optional CSS class for the img tag
 * @returns {string} HTML string
 */
export function renderBotIcon(iconValue, className = '') {
    if (isImageIcon(iconValue)) {
        const classAttr = className ? ` class="${className}"` : '';
        // Styling is handled by parent container CSS (.chat-widget-avatar img, .chat-nudge-icon img, etc.)
        return `<img src="${iconValue}" alt="Bot"${classAttr}>`;
    }
    return iconValue || '🤖';
}

// Get widget styles (scoped to shadow DOM)
export function getStyles(config) {
    return `
/* All your widget CSS goes here - it won't affect the parent page */
.chat-widget-container {
    position: fixed;
    bottom: 20px;
    right: 20px;
    z-index: 2147483647;
    pointer-events: auto;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif;
}

.chat-widget-toggle {
    width: 60px;
    height: 60px;
    border-radius: 30px;
    background: ${config.color};
    border: none;
    cursor: pointer;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    position: relative;
}

.chat-widget-toggle:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
}

.chat-widget-toggle svg {
    width: 28px;
    height: 28px;
    fill: white;
}

.chat-widget-badge {
    position: absolute;
    top: -5px;
    right: -5px;
    background: #ff4444;
    color: white;
    border-radius: 10px;
    padding: 2px 6px;
    font-size: 12px;
    font-weight: bold;
    display: none;
}

.chat-widget-badge.visible {
    display: block;
}

.chat-widget-window {
    position: absolute;
    bottom: 80px;
    right: 0;
    width: 380px;
    height: min(${config.maxHeightDesktop}, calc(100vh - 160px));
    min-height: 300px;
    max-height: min(${config.maxHeightDesktop}, calc(100vh - 160px));
    background: white;
    border-radius: 16px;
    border: 1px solid black;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    display: none;
    flex-direction: column;
    overflow: hidden;
    animation: slideUp 0.3s ease;
}

.chat-widget-resize-handle {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 12px;
    cursor: ns-resize;
    z-index: 10;
    display: flex;
    align-items: center;
    justify-content: center;
}

.chat-widget-resize-handle::after {
    content: '';
    width: 40px;
    height: 4px;
    background: rgba(255, 255, 255, 0.5);
    border-radius: 2px;
    transition: background 0.2s;
}

.chat-widget-resize-handle:hover::after {
    background: rgba(255, 255, 255, 0.8);
}

.chat-widget-window.visible {
    display: flex;
}

@keyframes slideUp {
    from { opacity: 0; transform: translateY(20px); }
    to { opacity: 1; transform: translateY(0); }
}

.chat-widget-header {
    background: ${config.color};
    color: white;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    transition: padding 0.3s ease;
}

.chat-widget-header.compact {
    padding: 12px 20px;
}

.chat-widget-header-content {
    display: flex;
    align-items: center;
    gap: 12px;
}

.chat-widget-avatar {
    width: 40px;
    height: 40px;
    border-radius: 20px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
    overflow: hidden;
}

.chat-widget-avatar img {
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
}

.chat-widget-header-text h3 {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
}

.chat-widget-header-text p {
    margin: 2px 0 0 0;
    font-size: 13px;
    opacity: 0.9;
}

.chat-widget-back {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    opacity: 0;
    transition: opacity 0.2s;
    display: none;
    margin-right: 12px;
    flex-shrink: 0;
}

.chat-widget-back.visible {
    opacity: 0.9;
    display: block;
}

.chat-widget-back:hover {
    opacity: 1;
}

.chat-widget-back svg {
    width: 20px;
    height: 20px;
    fill: white;
}

.chat-widget-close {
    background: none;
    border: none;
    color: white;
    cursor: pointer;
    padding: 4px;
    opacity: 0.9;
    transition: opacity 0.2s;
}

.chat-widget-close:hover {
    opacity: 1;
}

.chat-widget-close svg {
    width: 20px;
    height: 20px;
    fill: white;
}

.chat-widget-messages {
    flex: 1;
    overflow-y: auto;
    padding: 20px;
    background: #f5f5f5;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.chat-widget-messages::-webkit-scrollbar {
    width: 6px;
}

.chat-widget-messages::-webkit-scrollbar-track {
    background: transparent;
}

.chat-widget-messages::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
}

.chat-widget-messages::-webkit-scrollbar-thumb:hover {
    background: #aaa;
}

.chat-message {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    animation: messageSlideIn 0.3s ease;
}

@keyframes messageSlideIn {
    from { opacity: 0; transform: translateY(10px); }
    to { opacity: 1; transform: translateY(0); }
}

.chat-message.user {
    flex-direction: row-reverse;
}

.chat-message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 16px;
    background: ${config.color};
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    font-weight: 600;
    flex-shrink: 0;
    overflow: hidden;
}

.chat-message-avatar img {
    max-width: 80%;
    max-height: 80%;
    object-fit: contain;
}

.chat-message.user .chat-message-avatar {
    background: #4CAF50;
}

.chat-message-content {
    max-width: 75%;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.chat-message-bubble {
    padding: 12px 16px;
    border-radius: 18px;
    word-wrap: break-word;
    line-height: 1.4;
    font-size: 14px;
}

.chat-message-bubble code {
    background: rgba(0, 0, 0, 0.1);
    padding: 2px 6px;
    border-radius: 3px;
    font-family: 'Courier New', Courier, monospace;
    font-size: 13px;
}

.chat-message-bubble pre {
    background: rgba(0, 0, 0, 0.1);
    padding: 8px 12px;
    border-radius: 6px;
    overflow-x: auto;
    margin: 8px 0;
}

.chat-message-bubble pre code {
    background: none;
    padding: 0;
    font-size: 12px;
    display: block;
}

.chat-message-bubble a {
    color: inherit;
    text-decoration: underline;
}

.chat-message-bubble a.vehicle-link-btn {
    display: inline-block;
    background: ${config.color};
    color: white;
    text-decoration: none;
    padding: 8px 16px;
    border-radius: 6px;
    font-size: 13px;
    font-weight: 500;
    margin: 4px 0;
    transition: opacity 0.2s;
}

.chat-message-bubble a.vehicle-link-btn:hover {
    opacity: 0.85;
}

.option-buttons-container {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
    justify-content: center; /* Center the buttons */
}

.option-btn-compact {
    display: inline-block;
    background: ${config.color};
    color: white;
    border: none;
    padding: 10px 18px;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: normal;
    word-wrap: break-word;
    max-width: calc(100% - 56px); /* Account for container margin and some breathing room */
    text-align: center;
    line-height: 1.4;
}

.option-btn-compact:hover:not(:disabled) {
    opacity: 0.85;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
}

.option-btn-compact:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.chat-message-bubble strong {
    font-weight: 600;
}

.chat-message-bubble em {
    font-style: italic;
}

.chat-message.user .chat-message-bubble code {
    background: rgba(255, 255, 255, 0.2);
}

.chat-message.user .chat-message-bubble pre {
    background: rgba(255, 255, 255, 0.2);
}

.chat-message.assistant .chat-message-bubble {
    background: white;
    color: #333;
    border-bottom-left-radius: 4px;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.chat-message.user .chat-message-bubble {
    background: ${config.color};
    color: white;
    border-bottom-right-radius: 4px;
    margin-left: auto;
}

.chat-message.system .chat-message-bubble {
    background: #f0f0f0;
    color: #666;
    border-radius: 12px;
    text-align: center;
    font-size: 13px;
    font-style: italic;
    margin: 8px auto;
}

.chat-message.system {
    justify-content: center;
}

.chat-message.system .chat-message-avatar {
    display: none;
}

.chat-message.system .chat-message-time {
    display: none;
}

.chat-message-time {
    font-size: 11px;
    color: #999;
    padding: 0 8px;
}

.chat-message.user .chat-message-time {
    text-align: right;
}

.typing-indicator {
    display: none;
    align-items: center;
    gap: 8px;
    padding: 12px 16px;
    background: white;
    border-radius: 18px;
    border-bottom-left-radius: 4px;
    width: fit-content;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.typing-indicator.visible {
    display: flex;
}

.typing-indicator span {
    width: 8px;
    height: 8px;
    border-radius: 4px;
    background: ${config.color};
    animation: typing 1.4s infinite;
}

.typing-indicator span:nth-child(2) {
    animation-delay: 0.2s;
}

.typing-indicator span:nth-child(3) {
    animation-delay: 0.4s;
}

@keyframes typing {
    0%, 60%, 100% { transform: translateY(0); opacity: 0.5; }
    30% { transform: translateY(-10px); opacity: 1; }
}

.chat-widget-input {
    padding: 16px;
    background: white;
    border-top: 1px solid #e0e0e0;
    display: flex;
    gap: 8px;
    align-items: flex-end;
}

.chat-widget-input textarea {
    flex: 1;
    border: 1px solid #ddd;
    border-radius: 20px;
    padding: 10px 16px;
    font-size: 14px;
    font-family: inherit;
    resize: none;
    max-height: 100px;
    min-height: 40px;
    outline: none;
    transition: border-color 0.2s;
}

.chat-widget-input textarea:focus {
    border-color: ${config.color};
}

.chat-widget-input textarea::placeholder {
    color: #999;
}

.chat-widget-send-btn {
    width: 40px;
    height: 40px;
    border-radius: 20px;
    background: ${config.color};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: transform 0.2s, opacity 0.2s;
    flex-shrink: 0;
}

.chat-widget-send-btn:hover:not(:disabled) {
    transform: scale(1.05);
}

.chat-widget-send-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.chat-widget-send-btn svg {
    width: 20px;
    height: 20px;
    fill: white;
}

.chat-widget-welcome {
    text-align: center;
    padding: 40px 20px;
    color: #666;
}

.chat-widget-welcome-icon {
    font-size: 48px;
    margin-bottom: 16px;
}

.chat-widget-welcome h4 {
    margin: 0 0 8px 0;
    color: #333;
    font-size: 18px;
}

.chat-widget-welcome p {
    margin: 0;
    font-size: 14px;
    line-height: 1.5;
}

.chat-error-message {
    background: #ffebee;
    color: #c62828;
    padding: 8px 12px;
    border-radius: 12px;
    font-size: 13px;
    text-align: center;
    margin: 8px 0;
}

@media (max-width: 480px) {
    .chat-widget-window {
        width: 80vw;
        height: 70vh;
        position: fixed !important;
        left: 50% !important;
        top: 50% !important;
        transform: translate(-50%, -50%) !important;
        right: auto !important;
        bottom: auto !important;
        z-index: 2147483647;
        animation: fadeIn 0.3s ease !important;
    }

    .chat-widget-resize-handle {
        display: none;
    }

    @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
    .chat-widget-container {
        right: 20px;
        bottom: 20px;
    }
}

.chat-mobile-notification {
    position: fixed;
    bottom: 100px;
    right: 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 12px 16px;
    display: none;
    align-items: center;
    gap: 12px;
    max-width: calc(100vw - 40px);
    animation: slideInRight 0.3s ease;
    z-index: 2147483646;
}

.chat-mobile-notification.visible {
    display: flex;
}

@keyframes slideInRight {
    from { opacity: 0; transform: translateX(20px); }
    to { opacity: 1; transform: translateX(0); }
}

.chat-mobile-notification-icon {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    background: ${config.color};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
}

.chat-mobile-notification-content {
    flex: 1;
    min-width: 0;
}

.chat-mobile-notification-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.chat-mobile-notification-text {
    font-size: 12px;
    color: #666;
    margin: 2px 0 0 0;
}

.chat-mobile-notification-actions {
    display: flex;
    gap: 8px;
    flex-shrink: 0;
}

.chat-mobile-notification-btn {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: #666;
    transition: color 0.2s;
}

.chat-mobile-notification-btn:hover {
    color: ${config.color};
}

.chat-mobile-notification-btn svg {
    width: 20px;
    height: 20px;
    fill: currentColor;
}

/* Desktop Nudge Bubble - shows above launcher when initialDisplayMode is "nudge" */
.chat-nudge {
    position: fixed;
    bottom: 100px;
    right: 20px;
    background: white;
    border-radius: 12px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    padding: 12px 16px;
    display: none;
    align-items: center;
    gap: 12px;
    max-width: 280px;
    animation: slideInRight 0.3s ease;
    z-index: 2147483646;
}

.chat-nudge.visible {
    display: flex;
}

.chat-nudge.dismissing {
    animation: slideOutRight 0.3s ease forwards;
}

@keyframes slideOutRight {
    from { opacity: 1; transform: translateX(0); }
    to { opacity: 0; transform: translateX(20px); }
}

.chat-nudge-icon {
    width: 36px;
    height: 36px;
    border-radius: 18px;
    background: white;
    border: 2px solid ${config.color};
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 18px;
    flex-shrink: 0;
    overflow: hidden;
    box-sizing: border-box;
}

.chat-nudge-icon img {
    max-width: 75%;
    max-height: 75%;
    object-fit: contain;
}

.chat-nudge-content {
    flex: 1;
    min-width: 0;
    cursor: pointer;
}

.chat-nudge-title {
    font-size: 14px;
    font-weight: 600;
    color: #333;
    margin: 0;
}

.chat-nudge-text {
    font-size: 12px;
    color: #666;
    margin: 2px 0 0 0;
}

.chat-nudge-close {
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: #999;
    transition: color 0.2s;
    flex-shrink: 0;
}

.chat-nudge-close:hover {
    color: #333;
}

.chat-nudge-close svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
}

.redirect-btn {
    padding: 10px 16px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    flex: 1;
}

.redirect-yes {
    background: ${config.color};
    color: white;
}

.redirect-yes:hover {
    opacity: 0.9;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(102, 126, 234, 0.4);
}

.redirect-no {
    background: #f0f0f0;
    color: #666;
}

.redirect-no:hover {
    background: #e0e0e0;
}

.redirect-btn:active {
    transform: translateY(0);
}

/* User Details Capture Form */
.user-details-form {
    padding: 20px;
    background: white;
    border-radius: 12px;
    margin: 10px;
}

.user-details-intro {
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    margin-bottom: 20px;
    padding: 15px;
    background: #f8f9fa;
    border-radius: 8px;
}

.user-details-field {
    margin-bottom: 15px;
}

.user-details-label {
    display: block;
    font-size: 12px;
    color: #666;
    margin-bottom: 5px;
    font-weight: 500;
}

.user-details-input {
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    font-family: inherit;
    transition: border-color 0.2s;
    box-sizing: border-box;
}

/* Ensure date and time inputs use consistent font size */
.user-details-input[type="date"],
.user-details-input[type="time"] {
    font-size: 14px;
}

.user-details-input:focus {
    outline: none;
    border-color: ${config.color};
}

.user-details-input.error {
    border-color: #e74c3c;
}

.user-details-error {
    color: #e74c3c;
    font-size: 12px;
    margin-top: 4px;
    display: none;
}

.user-details-error.visible {
    display: block;
}

.user-details-phone-wrapper {
    display: flex;
    gap: 8px;
}

.user-details-country-code {
    flex: 0 0 70px;
    padding: 10px;
    border: 1px solid #ddd;
    border-radius: 6px;
    font-size: 14px;
    background: #f8f9fa;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 4px;
}

.user-details-phone-input {
    flex: 1;
}

.user-details-consent {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 15px 0;
    font-size: 13px;
    color: #666;
}

.user-details-consent input[type="checkbox"] {
    margin-top: 3px;
    width: 18px;
    height: 18px;
    cursor: pointer;
    flex-shrink: 0;
}

.user-details-consent label {
    cursor: pointer;
    line-height: 1.5;
}

.user-details-consent a {
    color: ${config.color};
    text-decoration: none;
}

.user-details-consent a:hover {
    text-decoration: underline;
}

.user-details-actions {
    display: flex;
    gap: 10px;
    margin-top: 20px;
}

.user-details-btn {
    flex: 1;
    padding: 12px 20px;
    border: none;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
}

.user-details-submit {
    background: ${config.color};
    color: white;
}

.user-details-submit:hover {
    opacity: 0.9;
    transform: translateY(-1px);
}

.user-details-submit:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
}

.user-details-decline {
    background: #f0f0f0;
    color: #666;
}

.user-details-decline:hover {
    background: #e0e0e0;
}

/* User Details Summary (for confirmation) */
.user-details-summary {
    background: #f8f9fa;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 15px;
    margin: 15px 0;
}

.user-details-summary-item {
    padding: 8px 0;
    font-size: 14px;
    color: #333;
}

.user-details-summary-item strong {
    color: #667eea;
    font-weight: 600;
    margin-right: 8px;
}

/* Suggested Questions */
.suggested-questions-container {
    padding: 20px;
    background: white;
    border-radius: 12px;
    margin: 10px;
}

.suggested-questions-title {
    font-size: 16px;
    font-weight: 600;
    color: #333;
    margin-bottom: 15px;
    text-align: center;
}

.suggested-questions-list {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.suggested-question-btn {
    padding: 12px 16px;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    background: white;
    color: #333;
    font-size: 14px;
    text-align: left;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 10px;
}

.suggested-question-btn:hover {
    background: #f8f9fa;
    border-color: ${config.color};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.suggested-question-btn::before {
    content: "💬";
    font-size: 18px;
    flex-shrink: 0;
}

/* Branding Footer */
.chat-widget-branding {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    background: #f8f9fa;
    border-top: 1px solid #e0e0e0;
    gap: 6px;
}

.chat-widget-branding a {
    display: flex;
    align-items: center;
    gap: 6px;
    text-decoration: none;
    color: #666;
    font-size: 11px;
    transition: color 0.2s;
}

.chat-widget-branding a:hover {
    color: #0f58a6;
}

.chat-widget-branding-logo {
    height: 16px;
    width: auto;
    opacity: 0.7;
    transition: opacity 0.2s;
}

.chat-widget-branding a:hover .chat-widget-branding-logo {
    opacity: 1;
}

.chat-widget-branding-text {
    font-weight: 500;
}
    `;
}

// Get widget HTML structure
export function getWidgetHTML(config) {
    return `
        <div class="chat-mobile-notification">
            <div class="chat-mobile-notification-icon">🤖</div>
            <div class="chat-mobile-notification-content">
                <p class="chat-mobile-notification-title">${config.mobileNotificationTitle}</p>
                <p class="chat-mobile-notification-text">${config.mobileNotificationText}</p>
            </div>
            <div class="chat-mobile-notification-actions">
                <button class="chat-mobile-notification-btn" id="openChatFromNotif" aria-label="Open chat">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/>
                    </svg>
                </button>
                <button class="chat-mobile-notification-btn" id="dismissNotif" aria-label="Dismiss">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                </button>
            </div>
        </div>

        <div class="chat-nudge">
            <div class="chat-nudge-icon">${renderBotIcon(config.botIcon)}</div>
            <div class="chat-nudge-content" id="openChatFromNudge">
                <p class="chat-nudge-title">${config.nudgeTitle}</p>
                <p class="chat-nudge-text">${config.nudgeText}</p>
            </div>
            <button class="chat-nudge-close" id="dismissNudge" aria-label="Dismiss">
                <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
            </button>
        </div>

        <button class="chat-widget-toggle" aria-label="Toggle chat">
            <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z"/>
            </svg>
            <span class="chat-widget-badge"></span>
        </button>

        <div class="chat-widget-window">
            <div class="chat-widget-resize-handle" aria-hidden="true"></div>
            <div class="chat-widget-header">
                <button class="chat-widget-back" aria-label="Back to questions">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z"/>
                    </svg>
                </button>
                <div class="chat-widget-header-content">
                    <div class="chat-widget-avatar">${renderBotIcon(config.botIcon)}</div>
                    <div class="chat-widget-header-text">
                        <h3>${config.botName}</h3>
                        <p>${config.botStatus}</p>
                    </div>
                </div>
                <button class="chat-widget-close" aria-label="Close chat">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                </button>
            </div>

            <div class="chat-widget-messages" id="chatMessages">
            </div>

            <div class="chat-widget-input">
                <textarea
                    id="chatInput"
                    placeholder="${config.placeholder}"
                    rows="1"
                    maxlength="1000"
                ></textarea>
                <button class="chat-widget-send-btn" id="chatSendBtn" aria-label="Send message">
                    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/>
                    </svg>
                </button>
            </div>

            <div class="chat-widget-branding">
                <a href="https://www.voxum.ca" target="_blank" rel="noopener noreferrer">
                    <svg class="chat-widget-branding-logo" viewBox="0 0 428 454" xmlns="http://www.w3.org/2000/svg">
                        <defs><style>.vx-icon{fill:#0f58a6;}</style></defs>
                        <g transform="translate(-83, -76)">
                            <path class="vx-icon" d="M174.6,76.2c51.2-4.5,107.9,2.8,159.7-.3,53.5,6.1,90.6,48.3,93.6,101.4,2.1,37.3,2.2,88.8,0,126-1.9,32.4-16.3,62.2-42.7,81.3-43.6,31.6-106.8,12.9-157.4,18.6-2.6.6-4.5,2-6.7,3.3-21.9,13-44.3,35.1-66.1,46.9-8.7,4.7-17.2,1.5-18.9-8.4-2.7-15.9,1.9-36.8-.7-53.3-30.3-17.9-49.7-48.1-52.5-83.5,2.4-45.1-3-93.2,0-138,3.3-48,43.7-90,91.8-94.2ZM155.8,427.9l58.3-41.2,8.7-4.3c42.4-5.9,107.7,10.7,145-11,26.5-15.4,37.6-42.2,39.1-71.9,1.9-35.9,1.9-83.1,0-119.1-2.2-43.1-30.5-80.3-75.5-83.5-50.1-3.6-104.5,2.8-155,0-38,4.6-68.7,35.3-72.4,73.6,2.1,44.7-3.3,92.6-.1,136.9,1.5,20.6,10.9,41.7,26.5,55.5,6.9,6.1,25.5,13.4,25.5,22.5v42.5Z"/>
                            <path class="vx-icon" d="M188.6,150.7c.5-.4,1.1-3,2.2-4.3,16.6-18.6,43.8-25.2,65.3-10.6,16.6-12.3,41.2-9,56.7,3.6,3.3,2.7,7.7,9.4,10,11,2.6,1.7,8.6,2.5,12,4,16.9,7.7,29.6,26.6,31,45,.3,3.4-1.1,10.2-.7,11.9.2.9,6.1,8.2,7.3,10.6,11.2,22.6,5.7,50.4-12.5,67.6-2,1.9-5.6,3.2-7,5s-3.6,8.3-5.6,11.4c-6.5,10.7-19.3,20.5-32,22s-9.4-.6-12.7.3-5.5,3.8-8,5c-13.7,6-31.2,4.7-42.2-6-27.8,20.8-71.7,7.8-78.5-27.7-35.1-7.2-52.5-46.2-34.4-77.4,1.4-2.4,6.9-8.6,7.2-9.8,1-3.2-1-8.6-.8-12.8.7-17.1,12.1-35.8,27.3-43.7,3.4-1.8,14.4-4.3,15.5-5ZM178.6,280.7c4.6-9.3,10.4-19.5,21.8-20.7,5.7-.6,26.8,2.5,27.4,9.4-.6,5.4-7.8,8.4-12.6,8.5s-8.5-4.3-15.6.3c-6.1,4-7.6,13-6.4,19.7,4.1,23.8,41.5,28.6,53.8,8.7l.8-149.3c-2.7-12.8-19.8-12.5-29.7-8.6-17,6.6-22.2,25.8-8.4,38.7,6.6,6.2,10.9,1.8,17.3,3.7,6.2,1.8,5.5,14.3-3.9,15.7-22,3.3-36.6-14.4-37.4-34.9-29.7,8.8-27.6,56.7,5.5,57.1,2,0,4-1.1,5.9-1,8.7.3,12,13.9,3.6,16.5s-13.2,1-17.4.3c-7.2-1.2-14.3-5.5-20.1-9.9-6.5-4.9-7-11-11.6,2.5-6.6,19.7,5.5,42,26.9,43.4ZM350.8,270.9c11-9.1,11.5-28.6,5.2-40.7-2-3.9-9.5-10.4-10-12-.7-2.4,1.3-13.8.7-18.7-1.3-11-10.1-23.2-19.9-28.1-4.8-2.4-14.6-3.5-17-5-1.1-.7-2.9-5.6-4.5-7.5-9.2-11.4-29.1-18.6-39.8-5.7l-.8,155.3c1.7,18.3,33.6,11.1,27.9-7.9-1.3-4.2-9.1-2.4-9.8-10.2-.9-9.6,7.1-11.1,14.7-7.7,10.1,4.6,13.4,17,12.3,27.2,19.3,0,34-24.8,22.4-40.9-3.6-5-14.4-6.9-10.2-15.3,6.8-13.8,27.2,8.2,28.8,17.2Z"/>
                            <path class="vx-icon" d="M282.1,185.2c5.2-5.2,17.1-1.1,22.6,2.3,23.6,14.4,18.9,52.2-6.9,60.9-7.9,2.7-19.1.7-16.8-9.7,1.5-6.9,8-5.1,12.8-7.2,11.2-4.9,11.9-22.9,1.9-29.9-5-3.5-6.7-1.7-11.4-2.6-5.6-1-5.6-10.4-2.2-13.7Z"/>
                        </g>
                    </svg>
                    <span class="chat-widget-branding-text">Powered by Voxum</span>
                </a>
            </div>
        </div>
    `;
}

// Get welcome HTML
export function getWelcomeHTML(config) {
    return `
        <div class="chat-widget-welcome">
            <div class="chat-widget-welcome-icon">👋</div>
            <h4>Welcome!</h4>
            <p>${config.welcomeMessage}</p>
        </div>
    `;
}

// Create widget DOM structure with Shadow DOM
export function createWidget(config) {
    // Create container with Shadow DOM
    const hostElement = document.createElement('div');
    hostElement.id = 'chat-widget-host';
    // Apply styles to host element to ensure it's above CMS overlays (Squarespace, Wix, etc.)
    // Host is fixed, full viewport, high z-index, but pointer-events:none so it doesn't block the page
    // The actual widget elements inside Shadow DOM have pointer-events:auto
    hostElement.style.cssText = 'position:fixed;inset:0;z-index:2147483647;pointer-events:none;';
    document.body.appendChild(hostElement);

    // Attach shadow DOM
    const shadowRoot = hostElement.attachShadow({ mode: 'open' });

    // Inject styles with ID so we can update them later
    const style = document.createElement('style');
    style.id = 'chat-widget-styles';
    style.textContent = getStyles(config);
    shadowRoot.appendChild(style);

    // Create widget HTML
    const container = document.createElement('div');
    container.className = 'chat-widget-container';
    container.innerHTML = getWidgetHTML(config);

    shadowRoot.appendChild(container);

    // Return references to elements and shadow root
    return {
        shadowRoot,
        container,
        messagesContainer: container.querySelector('#chatMessages'),
        input: container.querySelector('#chatInput'),
        sendBtn: container.querySelector('#chatSendBtn'),
        toggleBtn: container.querySelector('.chat-widget-toggle'),
        closeBtn: container.querySelector('.chat-widget-close'),
        backBtn: container.querySelector('.chat-widget-back'),
        header: container.querySelector('.chat-widget-header'),
        window: container.querySelector('.chat-widget-window'),
        resizeHandle: container.querySelector('.chat-widget-resize-handle'),
        mobileNotification: container.querySelector('.chat-mobile-notification'),
        openFromNotifBtn: container.querySelector('#openChatFromNotif'),
        dismissNotifBtn: container.querySelector('#dismissNotif'),
        nudge: container.querySelector('.chat-nudge'),
        openFromNudgeBtn: container.querySelector('#openChatFromNudge'),
        dismissNudgeBtn: container.querySelector('#dismissNudge')
    };
}

// Update widget styles after config changes (e.g., after fetching server config)
export function updateStyles(shadowRoot, config) {
    const styleElement = shadowRoot.getElementById('chat-widget-styles');
    if (styleElement) {
        styleElement.textContent = getStyles(config);
    }
}

// Update bot icon in all rendered elements after config changes
export function updateBotIconElements(shadowRoot, config) {
    const iconHTML = renderBotIcon(config.botIcon);

    // Update header avatar
    const headerAvatar = shadowRoot.querySelector('.chat-widget-avatar');
    if (headerAvatar) {
        headerAvatar.innerHTML = iconHTML;
    }

    // Update nudge icon
    const nudgeIcon = shadowRoot.querySelector('.chat-nudge-icon');
    if (nudgeIcon) {
        nudgeIcon.innerHTML = iconHTML;
    }

    // Note: Message avatars are not updated here since they're part of the conversation
    // history and changing them mid-conversation could be confusing
}

// Update nudge text after config changes (e.g., after server config is loaded)
export function updateNudgeText(shadowRoot, config) {
    const nudgeTitle = shadowRoot.querySelector('.chat-nudge-title');
    if (nudgeTitle) {
        nudgeTitle.textContent = config.nudgeTitle || 'Need help?';
    }

    const nudgeText = shadowRoot.querySelector('.chat-nudge-text');
    if (nudgeText) {
        nudgeText.textContent = config.nudgeText || 'Chat with our AI assistant';
    }
}

// Create HTML for a single message
export function createMessageHTML(message, config) {
    const time = formatTime(message.timestamp);
    const avatar = message.role === 'user' ? '👤' : renderBotIcon(config.botIcon);
    const content = message.role === 'assistant' ? renderMarkdown(message.content) : escapeHtml(message.content);

    return `
        <div class="chat-message ${message.role}">
            <div class="chat-message-avatar">${avatar}</div>
            <div class="chat-message-content">
                <div class="chat-message-bubble">${content}</div>
                <div class="chat-message-time">${time}</div>
            </div>
        </div>
    `;
}

// Render all messages
export function renderMessages(messages, messagesContainer, config) {
    if (messages.length === 0) {
        messagesContainer.innerHTML = getWelcomeHTML(config);
        return;
    }

    messagesContainer.innerHTML = messages.map(msg =>
        createMessageHTML(msg, config)
    ).join('');
}

// Show typing indicator
export function showTypingIndicator(shadowRoot, messagesContainer, config) {
    const existingIndicator = shadowRoot.getElementById('typingIndicator');
    if (existingIndicator) return;

    const indicator = document.createElement('div');
    indicator.className = 'chat-message assistant';
    indicator.innerHTML = `
        <div class="chat-message-avatar">${renderBotIcon(config.botIcon)}</div>
        <div class="typing-indicator visible">
            <span></span>
            <span></span>
            <span></span>
        </div>
    `;
    indicator.id = 'typingIndicator';
    messagesContainer.appendChild(indicator);
}

// Hide typing indicator
export function hideTypingIndicator(shadowRoot) {
    const indicator = shadowRoot.getElementById('typingIndicator');
    if (indicator) {
        indicator.remove();
    }
}

// Scroll to bottom of messages
export function scrollToBottom(messagesContainer) {
    requestAnimationFrame(() => {
        messagesContainer.scrollTop = messagesContainer.scrollHeight;
    });
}

// Scroll to show recent messages (especially intro messages before forms)
export function scrollToShowRecentMessages(messagesContainer, targetMessageElement = null) {
    requestAnimationFrame(() => {
        let messageToScrollTo = targetMessageElement;

        if (!messageToScrollTo) {
            // No specific target, find the last message bubble
            const messages = messagesContainer.querySelectorAll('.chat-message-bubble');

            if (messages.length === 0) {
                // No messages, just scroll to bottom
                scrollToBottom(messagesContainer);
                return;
            }

            // Get the parent .chat-message element of the last bubble
            const lastBubble = messages[messages.length - 1];
            messageToScrollTo = lastBubble.closest('.chat-message');
        }

        if (!messageToScrollTo) {
            // Fallback to scrolling to bottom if we can't find a message
            scrollToBottom(messagesContainer);
            return;
        }

        // Scroll to show the target message at the top of the visible area
        // This ensures the intro message is visible even when the form is large
        const messageTop = messageToScrollTo.offsetTop;

        // Scroll so the message is visible near the top (with some padding)
        messagesContainer.scrollTop = Math.max(0, messageTop - 20);
    });
}

// Show error message
export function showError(messagesContainer, message) {
    const errorDiv = document.createElement('div');
    errorDiv.className = 'chat-error-message';
    errorDiv.textContent = message;
    messagesContainer.appendChild(errorDiv);
    scrollToBottom(messagesContainer);

    setTimeout(() => errorDiv.remove(), 5000);
}

// Open widget
export function open(window, input) {
    window.classList.add('visible');
    // Use setTimeout to ensure focus happens after DOM updates
    setTimeout(() => {
        input.focus();
    }, 100);
    // Clear the manually closed flag when user opens it
    saveManuallyClosed(false);
}

// Close widget
export function close(window) {
    window.classList.remove('visible');
    // Mark that user manually closed the widget
    saveManuallyClosed(true);
}

// Toggle widget
export function toggle(isOpen, window, input) {
    if (isOpen) {
        close(window);
    } else {
        open(window, input);
    }
    return !isOpen;
}

// Show mobile notification
export function showMobileNotification(mobileNotification) {
    mobileNotification.classList.add('visible');
}

// Hide mobile notification
export function hideMobileNotification(mobileNotification) {
    mobileNotification.classList.remove('visible');
}

// Show desktop nudge
export function showNudge(nudge) {
    nudge.classList.add('visible');
}

// Hide desktop nudge with optional animation
export function hideNudge(nudge, animate = false) {
    if (animate) {
        nudge.classList.add('dismissing');
        setTimeout(() => {
            nudge.classList.remove('visible');
            nudge.classList.remove('dismissing');
        }, 300);
    } else {
        nudge.classList.remove('visible');
    }
}

// Show back button and compact header (chat mode)
export function showBackButton(backBtn, header) {
    backBtn.classList.add('visible');
    header.classList.add('compact');
}

// Hide back button and expand header (welcome mode)
export function hideBackButton(backBtn, header) {
    backBtn.classList.remove('visible');
    header.classList.remove('compact');
}
